import { ArrowClockwiseIcon, LightningIcon, XIcon } from '@phosphor-icons/react'
import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from 'urql'
import type { Event, WorkflowRun, WorkflowRunEvent } from 'app/generated/workflows'
import { Button } from 'app/components/ui/button'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import { cn } from 'app/lib/utils'
import type { WorkflowNode } from 'app/types/workflows'
import { notify, notifySuccess } from 'app/services/notify.client'
import { ContactSearchField } from './ContactSearchField'
import { type TriggerSpec, WhyDidntItFire } from './WhyDidntItFire'
import {
  CANCEL_WORKFLOW_RUN_MUTATION,
  ENROLLMENT_EVENT_QUERY,
  LIST_WORKFLOW_RUNS_QUERY,
  TRIGGER_WORKFLOW_ON_CONTACT_MUTATION,
  WORKFLOW_RUN_EVENTS_QUERY,
  WORKFLOW_TEST_CONTACTS_QUERY,
} from 'app/lib/workflows/graphql'
import { formatEventTimestamp, isWallClockEvent } from 'app/lib/workflows/format'
import { RUN_REASON_TEXT, runEventDetail, runEventStatus, runStall } from './runViz'
import { storedEventLabel } from 'app/lib/workflows/triggerCatalog'
import { CHAIN_NODE_SUFFIX } from 'app/lib/workflows/stepChecks'

type RunsPanelProps = {
  /** The backend workflow id (remoteId). Undefined when the workflow hasn't synced yet. */
  workflowId?: string
  locationId?: string
  locationTimezone?: string | null
  /** Whether the workflow is active. Draft workflows don't run, so an enrollment won't produce a run. */
  active?: boolean
  open: boolean
  onClose: () => void
  /** Called with the run being watched (or null) so the canvas can light up its nodes live. */
  onWatchRun?: (runId: string | null) => void
  /** The compiled trigger, for the "Why didn't it fire?" explainer. Absent when there's no trigger. */
  triggerSpec?: TriggerSpec
  triggerStartDate?: string
  nodes: WorkflowNode[]
}

const STATUS_TONE: Record<string, string> = {
  running: `bg-blue-100 text-blue-700`,
  waiting: `bg-amber-100 text-amber-800`,
  done: `bg-emerald-100 text-emerald-700`,
  completed: `bg-emerald-100 text-emerald-700`,
  exited: `bg-surface-strong text-subtle`,
  cancelled: `bg-surface-strong text-subtle`,
  skipped: `bg-surface-strong text-subtle`,
  failed: `bg-red-100 text-red-700`,
}

function StatusPill({ status }: { status: string }) {
  return (
    <span
      className={cn(
        `inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium capitalize`,
        STATUS_TONE[status] ?? `bg-surface-muted text-subtle`,
      )}
    >
      {status}
    </span>
  )
}

/** Slide-over showing a synced workflow's runs and, for a selected run, its per-node event timeline.
 *  Also directly enrolls a contact (triggerWorkflowOnContact). The "test this trigger" flow lives on the
 *  trigger node itself. */
export function RunsPanel({
  workflowId,
  locationId,
  locationTimezone,
  active,
  open,
  onClose,
  onWatchRun,
  triggerSpec,
  triggerStartDate,
  nodes,
}: RunsPanelProps) {
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null)
  // "Test on a contact" is a SEARCH, not a raw-id input (UX audit C4) — shared with the trigger's
  // Test tab via ContactSearchField.
  const [pickedContact, setPickedContact] = useState<{ id: string; name: string } | null>(null)

  // Selecting a run both opens its timeline here and tells the canvas to light up as that run executed.
  const watchRun = (runId: string | null) => {
    setSelectedRunId(runId)
    onWatchRun?.(runId)
  }

  // `workflowRuns` only exposes search/sort/pagination (introspected — no dedicated workflowId arg),
  // so filtering goes through the search DSL, same as workflowRunEvents below.
  const [runsResult, refetchRuns] = useQuery<{ workflowRuns: { hits: WorkflowRun[]; total: number } }>({
    query: LIST_WORKFLOW_RUNS_QUERY,
    variables: {
      search: workflowId ? `workflowId:${workflowId}` : ``,
      pagination: { limit: 50 },
      sort: { field: `startedAt`, direction: `DESC` },
    },
    pause: !open || !workflowId,
    requestPolicy: `cache-and-network`,
  })

  const [eventsResult, reexecuteEvents] = useQuery<{ workflowRunEvents: { hits: WorkflowRunEvent[] } }>({
    query: WORKFLOW_RUN_EVENTS_QUERY,
    variables: {
      search: `runId:${selectedRunId}`,
      pagination: { limit: 100 },
      sort: { field: `timestamp`, direction: `ASC` },
    },
    pause: !selectedRunId,
  })

  const runs = runsResult.data?.workflowRuns.hits ?? []
  // Runs only carry a contactId, so resolve the page's contacts in one search. Keyed on the sorted
  // id set so the 4s poll doesn't refetch names while the set is unchanged.
  const contactIdsKey = [...new Set(runs.map((run) => run.contactId).filter(Boolean))].sort().join(`,`)
  const contactVariables = useMemo(
    () => ({
      search: `id:(${contactIdsKey
        .split(`,`)
        .map((id) => `"${id}"`)
        .join(` OR `)})`,
      pagination: { limit: 50 },
    }),
    [contactIdsKey],
  )
  const [contactsResult] = useQuery({
    query: WORKFLOW_TEST_CONTACTS_QUERY,
    variables: contactVariables,
    pause: !contactIdsKey,
  })
  const contactNames = useMemo(
    () =>
      new Map(
        (contactsResult.data?.contacts.hits ?? []).map((hit) => [
          hit.id,
          hit.name || formatPhoneNumber(hit.cellPhone) || `Unnamed contact`,
        ]),
      ),
    [contactsResult.data],
  )
  const contactLabel = (contactId?: string | null) =>
    !contactId
      ? `No contact`
      : (contactNames.get(contactId) ?? (contactsResult.fetching ? `Loading…` : `Unknown contact`))
  const nodeLabels = useMemo(() => new Map(nodes.map((node) => [node.id, node.data.label])), [nodes])

  const [triggerState, triggerOnContact] = useMutation(TRIGGER_WORKFLOW_ON_CONTACT_MUTATION)
  const [cancelState, cancelRun] = useMutation(CANCEL_WORKFLOW_RUN_MUTATION)

  // The backend enrolls runs (and advances steps) via a poller — not instantly — so without auto-refresh
  // a run that lands a few seconds after firing would stay invisible until a manual refresh. Poll the
  // list while the panel is open, and the open run's timeline while one is selected.
  useEffect(() => {
    if (!open || !workflowId) return
    const id = setInterval(() => refetchRuns({ requestPolicy: `network-only` }), 4000)
    return () => clearInterval(id)
  }, [open, workflowId, refetchRuns])
  useEffect(() => {
    if (!selectedRunId) return
    const id = setInterval(() => reexecuteEvents({ requestPolicy: `network-only` }), 4000)
    return () => clearInterval(id)
  }, [selectedRunId, reexecuteEvents])

  if (!open) return null

  const events = eventsResult.data?.workflowRunEvents.hits ?? []

  const onCancelRun = async (runId: string) => {
    const result = await cancelRun({ id: runId })
    if (result.error) notify(`Couldn't cancel: ${result.error.message}`)
    else {
      notifySuccess(`Run cancelled`)
      refetchRuns({ requestPolicy: `network-only` })
    }
  }

  const onTestFire = async () => {
    if (!workflowId || !pickedContact) return
    const result = await triggerOnContact({ workflowId, contactId: pickedContact.id })
    if (result.error) notify(`Couldn't enroll: ${result.error.message}`)
    else {
      notifySuccess(`${pickedContact.name} enrolled. Runs appear here once the engine processes them.`)
      setPickedContact(null)
      refetchRuns({ requestPolicy: `network-only` })
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <button type="button" aria-label="Close runs" className="flex-1 bg-overlay/20" onClick={onClose} />
      <div className="flex w-[440px] max-w-full flex-col border-l border-default bg-surface shadow-xl">
        <div className="flex items-center justify-between border-b border-default px-4 py-3">
          <div>
            <h2 className="text-sm font-semibold text-strong">Runs</h2>
            <p className="text-[11px] text-subtle">
              {workflowId ? `${runsResult.data?.workflowRuns.total ?? 0} total` : `Save this workflow to see runs`}
            </p>
          </div>
          <div className="flex items-center gap-1">
            {workflowId && (
              <Button
                variant="white"
                size="icon-sm"
                title="Refresh"
                onClick={() => refetchRuns({ requestPolicy: `network-only` })}
              >
                <ArrowClockwiseIcon size={16} weight="bold" />
              </Button>
            )}
            <Button variant="white" size="icon-sm" title="Close" onClick={onClose}>
              <XIcon size={16} weight="bold" />
            </Button>
          </div>
        </div>

        {!workflowId ? (
          <div className="flex flex-1 items-center justify-center p-6 text-center text-xs text-subtle">
            This workflow hasn’t been saved to the server yet. Save it, then enroll a contact to see runs.
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            {/* Test-fire on a contact */}
            <div className="border-b border-default bg-surface-subtle p-4">
              <label htmlFor="runs-contact" className="text-[11px] font-medium text-subtle">
                Test on a contact
              </label>
              <div className="mt-1.5 flex gap-2">
                <div className="min-w-0 flex-1">
                  <ContactSearchField
                    inputId="runs-contact"
                    locationId={locationId}
                    value={pickedContact?.id ?? ``}
                    onChange={(id, name) => setPickedContact(id ? { id, name: name ?? `Unnamed contact` } : null)}
                  />
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={!active || !pickedContact || triggerState.fetching}
                  onClick={onTestFire}
                >
                  Enroll
                </Button>
              </div>
              {active ? (
                <p className="mt-1.5 text-[11px] text-amber-700">
                  Enrolls the contact for real and can send live messages.
                </p>
              ) : (
                <p className="mt-1.5 text-[11px] text-amber-700">Drafts don’t run. Set it live first.</p>
              )}
            </div>

            {/* "Why didn't it fire?" — enrollment-decision explainer (see WhyDidntItFire). */}
            {workflowId && triggerSpec && (
              <WhyDidntItFire
                workflowId={workflowId}
                triggerStartDate={triggerStartDate}
                spec={triggerSpec}
                runs={runs}
                timeZone={locationTimezone}
              />
            )}

            {/* Runs list / timeline */}
            {selectedRunId ? (
              <RunTimeline
                run={runs.find((r) => r.id === selectedRunId)}
                contactLabel={contactLabel}
                nodeLabels={nodeLabels}
                events={events}
                timeZone={locationTimezone}
                fetching={eventsResult.fetching}
                cancelling={cancelState.fetching}
                onBack={() => watchRun(null)}
                onCancel={onCancelRun}
              />
            ) : runs.length === 0 ? (
              <div className="p-6 text-center text-xs text-subtle">
                {runsResult.fetching
                  ? `Loading…`
                  : `No runs yet. Enroll a contact above (or let the trigger fire) and runs will appear here.`}
              </div>
            ) : (
              <ul className="divide-y divide-subtle">
                {runs.map((run) => (
                  <li key={run.id}>
                    <button
                      type="button"
                      onClick={() => watchRun(run.id)}
                      className="flex w-full items-center justify-between gap-2 px-4 py-2.5 text-left hover:bg-surface-muted"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-xs font-medium text-default">{contactLabel(run.contactId)}</p>
                        {/* A stalled run used to render exactly like a healthy one, so "working" and
                            "dead" were indistinguishable on screen (TRK-5563). Say it instead. */}
                        <p
                          className={cn(
                            `truncate text-[10px]`,
                            runStall(run) ? `font-medium text-amber-700` : `text-subtle`,
                          )}
                        >
                          {runStall(run) ??
                            (run.exitReason
                              ? `Exited: ${RUN_REASON_TEXT[run.exitReason.toLowerCase()] ?? run.exitReason}`
                              : run.waitingFor
                                ? `Waiting: ${run.waitingFor}`
                                : `At: ${nodeLabels.get(run.currentNodeId?.replace(CHAIN_NODE_SUFFIX, ``) ?? run.currentNodeId) ?? run.currentNodeId}`)}
                        </p>
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1">
                        <StatusPill status={run.status} />
                        <span className="text-[10px] text-minimal">
                          {formatEventTimestamp(run.startedAt, false, locationTimezone)}
                        </span>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// A run is still cancellable while it's in flight.
const isActiveRun = (status?: string) => status === `running` || status === `waiting`

// Event.source is either "System" or "user <name>" (the change-log attribution format).
const eventSourceLabel = (source: string) => (source.startsWith(`user `) ? `by ${source.slice(5)}` : source)

/** "Enrolled by Form submitted · Aug 3, 4:17 PM · System" — the run's enrollment provenance,
 *  resolved from run.enrollmentEventId. Since the event-stream pivot this is the ONLY origin
 *  recorded on organically-enrolled runs (run.activityId is null), so surfacing it is what makes
 *  "why is this contact in here?" answerable at all. Runs without an event id (manual test
 *  enrollments, pre-pivot history) can't be told apart, so they get no line rather than a guess. */
function EnrollmentProvenance({ eventId, timeZone }: { eventId: string; timeZone?: string | null }) {
  const [{ data }] = useQuery<{ event?: Event }>({ query: ENROLLMENT_EVENT_QUERY, variables: { id: eventId } })
  const event = data?.event
  if (!event) return null
  return (
    <div className="flex items-center gap-1.5 border-b border-subtle bg-surface-subtle px-4 py-2 text-[11px] text-subtle">
      <LightningIcon size={12} weight="fill" className="shrink-0 text-amber-500" />
      <span className="min-w-0 truncate">
        Enrolled by <span className="font-medium text-default">{storedEventLabel(event.type, event.action)}</span>
        {/* Call events and most form submissions carry location wall-clock dates, not UTC (TRK-5396). */}
        {` · ${formatEventTimestamp(event.eventDate, isWallClockEvent(event), timeZone)} · ${eventSourceLabel(event.source)}`}
      </span>
    </div>
  )
}

function RunTimeline({
  run,
  contactLabel,
  nodeLabels,
  events,
  timeZone,
  fetching,
  cancelling,
  onBack,
  onCancel,
}: {
  run?: WorkflowRun
  contactLabel: (contactId?: string | null) => string
  nodeLabels: Map<string, string>
  events: WorkflowRunEvent[]
  timeZone?: string | null
  fetching: boolean
  cancelling: boolean
  onBack: () => void
  onCancel: (runId: string) => void
}) {
  return (
    <div>
      <div className="flex items-center gap-2 border-b border-default px-4 py-2.5">
        <Button variant="white" size="sm" onClick={onBack}>
          ← Runs
        </Button>
        {run && <StatusPill status={run.status} />}
        <span className="min-w-0 flex-1 truncate text-[11px] text-subtle">{contactLabel(run?.contactId)}</span>
        {run && isActiveRun(run.status) && (
          <Button variant="white" size="sm" disabled={cancelling} onClick={() => onCancel(run.id)}>
            Cancel
          </Button>
        )}
      </div>
      {run?.enrollmentEventId && <EnrollmentProvenance eventId={run.enrollmentEventId} timeZone={timeZone} />}
      {fetching && events.length === 0 ? (
        <div className="p-6 text-center text-xs text-subtle">Loading timeline…</div>
      ) : events.length === 0 ? (
        <div className="p-6 text-center text-xs text-subtle">No events recorded for this run yet.</div>
      ) : (
        <ol className="space-y-0 p-4">
          {events.map((event, i) => {
            const detail = runEventDetail(event)
            return (
              <li key={event.id} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <span className="mt-1 size-2 rounded-full bg-inverse/40" />
                  {i < events.length - 1 && <span className="w-px flex-1 bg-surface-strong" />}
                </div>
                <div className="pb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-default">
                      {nodeLabels.get(event.nodeId?.replace(CHAIN_NODE_SUFFIX, ``) ?? event.nodeId) ?? event.nodeType}
                    </span>
                    <StatusPill status={runEventStatus(event) ?? event.status} />
                  </div>
                  {detail && (
                    <p className={cn(`text-[11px]`, detail.tone === `failed` ? `text-red-700` : `text-subtle`)}>
                      {detail.text}
                    </p>
                  )}
                  <p className="text-[10px] text-minimal">{formatEventTimestamp(event.timestamp, false, timeZone)}</p>
                </div>
              </li>
            )
          })}
        </ol>
      )}
    </div>
  )
}
